import { useState } from 'react';
import { formatDuration, SOURCE_LABEL } from '../lib/protocol.js';
import type { Member, RoomState, Track } from '../lib/protocol.js';
import type { Jam } from '../lib/useJam.js';

type Props = {
  state: RoomState;
  jam: Jam;
  memberById: Map<string, Member>;
};

export function Queue({ state, jam, memberById }: Props) {
  const [showHistory, setShowHistory] = useState(false);
  const isHost = jam.me?.id === state.hostId;
  const groups = groupByRound(state.queue);

  return (
    <section className="queue">
      <header className="queue-head">
        <h2>
          キュー <span className="count">{state.queue.length}</span>
        </h2>
        {isHost && (
          <label className="basis">
            <select
              value={state.roundBasis}
              onChange={(e) =>
                jam.actions.setRoundBasis(e.target.value as RoomState['roundBasis'])
              }
            >
              <option value="pending">未再生曲数で回す</option>
              <option value="lifetime">累計投入数で回す</option>
            </select>
          </label>
        )}
      </header>

      {state.queue.length === 0 ? (
        <p className="empty">
          まだ曲がありません。リンクを貼るか曲名で検索して追加してください。
        </p>
      ) : (
        groups.map((group, index) => (
          <div key={group.round} className="round-group">
            <div className="round-label">{index + 1} 周目</div>
            <ul className="track-list">
              {group.tracks.map((track) => (
                <QueueRow
                  key={track.id}
                  track={track}
                  member={memberById.get(track.memberId)}
                  canRemove={isHost || track.memberId === jam.me?.id}
                  onRemove={() => jam.actions.remove(track.id)}
                />
              ))}
            </ul>
          </div>
        ))
      )}

      {state.history.length > 0 && (
        <div className="history">
          <button className="link-btn" onClick={() => setShowHistory((v) => !v)}>
            再生履歴 ({state.history.length}) {showHistory ? '▲' : '▼'}
          </button>
          {showHistory && (
            <ul className="track-list track-list-muted">
              {state.history.map((track, index) => (
                <li key={`${track.id}-${index}`} className="track-row">
                  <div className="track-row-main">
                    <div className="track-row-title">{track.title}</div>
                    <div className="track-row-sub">
                      {memberById.get(track.memberId)?.name ?? '退出したメンバー'}
                      {track.artist ? ` · ${track.artist}` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function QueueRow({
  track,
  member,
  canRemove,
  onRemove,
}: {
  track: Track;
  member: Member | undefined;
  canRemove: boolean;
  onRemove: () => void;
}) {
  return (
    <li className="track-row">
      {track.thumbnail ? (
        <img src={track.thumbnail} alt="" className="track-thumb" />
      ) : (
        <div className="track-thumb track-thumb-empty" />
      )}
      <div className="track-row-main">
        <div className="track-row-title">{track.title}</div>
        <div className="track-row-sub">
          <span className="owner">
            <span className="dot" style={{ background: member?.color ?? '#555' }} />
            {member?.name ?? '退出したメンバー'}
          </span>
          {track.artist && <span> · {track.artist}</span>}
          <span className={`badge badge-${track.source}`}>
            {SOURCE_LABEL[track.source]}
          </span>
        </div>
      </div>
      <div className="track-row-side">
        <span className="dur">{formatDuration(track.durationMs)}</span>
        {canRemove && (
          <button className="icon-btn" onClick={onRemove} title="キューから外す">
            ×
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * サーバ側の groupByRound と同じ区切り方。
 * 「1周目: A B C / 2周目: A B」と見せることで、ラウンドロビンが
 * どう回っているかがそのまま伝わる。
 */
function groupByRound(queue: Track[]): { round: number; tracks: Track[] }[] {
  const groups = new Map<number, Track[]>();
  for (const track of queue) {
    const list = groups.get(track.round);
    if (list) list.push(track);
    else groups.set(track.round, [track]);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([round, tracks]) => ({
      round,
      tracks: tracks.sort((a, b) => a.seq - b.seq),
    }));
}
